<template>
    <div class="home">
        <Header></Header>
        <div id="container">
            <FormKit type="multi-step" valid-step-icon="" tab-style="progress" :allow-incomplete="false"
                :before-step-change="stepChanged">
                <FormKit type="step" name="preparation">
                    <div class="stepContainer">
                        <h3>{{ preparationMessage }}</h3>
                        <div v-if="!isReady && currentAttempt < maxAttempt">
                            <b-spinner small type="grow"></b-spinner>
                        </div>
                    </div>
                    <b-button class="cancelBtn" v-on:click="cancel()">
                        Cancel
                    </b-button>
                    <FormKit type="hidden" value="" validation="required" v-model="prepared">
                    </FormKit>
                </FormKit>

                <FormKit type="step" name="solution 1">
                    <div class="stepContainer">
                        <h4>Calibration Solution 1: zero solution</h4>
                        <div class="buttons">
                            <b-button class="action" variant="primary" v-if="calibrationState !== 1"
                                v-on:click="calibrate(0)">{{ calibrationMessage }}</b-button>
                            <b-button class="cancelBtn" v-on:click="cancel()">
                                Cancel
                            </b-button>
                        </div>
                        <b-button class="action" variant="primary" disabled v-if="calibrationState === 1">
                            <b-spinner small type="grow"></b-spinner>
                            {{ calibrationMessage }}
                        </b-button>
                        <br>
                    </div>
                </FormKit>

                <FormKit type="step" name="solution 2">
                    <h4>Calibration Solution 2: phycocyanin high solution</h4>
                    <div class="buttons">
                        <b-button class="action" variant="primary" v-if="calibrationState !== 1"
                            v-on:click="calibrate(1)">{{
                                calibrationMessage }}
                        </b-button>
                        <b-button class="cancelBtn" v-on:click="cancel()">
                            Cancel
                        </b-button>
                    </div>

                    <b-button class="action" variant="primary" disabled v-if="calibrationState === 1">
                        <b-spinner small type="grow"></b-spinner>
                        {{ calibrationMessage }}
                    </b-button>
                    <br>
                </FormKit>

                <FormKit type="step" name="solution 3">
                    <div class="stepContainer">
                        <h4>Calibration Solution 3: chlorophyll-a high solution</h4>
                        <div class="buttons">
                            <b-button class="action" variant="primary" v-if="calibrationState !== 1"
                                v-on:click="calibrate(2)">{{
                                    calibrationMessage }}
                            </b-button>
                            <b-button class="cancelBtn" v-on:click="cancel()">
                                Cancel
                            </b-button>
                        </div>

                        <b-button class="action" variant="primary" disabled v-if="calibrationState === 1">
                            <b-spinner small type="grow"></b-spinner>
                            {{ calibrationMessage }}
                        </b-button>
                        <br>
                    </div>
                </FormKit>
                <FormKit type="step" name="solution 4">
                    <div class="stepContainer">
                        <h4>Calibration Solution 4: turbidity high solution</h4>
                        <div class="buttons">
                            <b-button class="action" variant="primary" v-if="calibrationState !== 1"
                                v-on:click="calibrate(3)">{{
                                    calibrationMessage }}
                            </b-button>
                            <b-button class="action" v-on:click="completeCalibration()" variant="primary">
                                Back to Main page
                            </b-button>
                        </div>

                        <b-button class="action" variant="primary" disabled v-if="calibrationState === 1">
                            <b-spinner small type="grow"></b-spinner>
                            {{ calibrationMessage }}
                        </b-button>
                        <br>

                        <br>
                    </div>
                </FormKit>
            </FormKit>
        </div>
        <div id="container">
            <b-table class="table" :items="items"></b-table>
        </div>

    </div>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import Header from "../components/Header.vue";
import { callCalibrationFunction, callCalibarationAwakeFunction, calibrationCallFunction, pingDevice } from "../utilities/calibrationUtilities";
import router from '../router';

const prepared = ref('');

const items = ref([
    { steps: "Solution 1", detail: '4.2 liters of DI water in the calibration pail: zero calibration of phycocyanin, chlorophyll-a, and turbidity', status: '' },
    { steps: "Solution 2", detail: 'Add 3 ml of the rhodamine solution of 125 mg/L concentration to the pail and stir thoroughly for 20 seconds', status: '' },
    { steps: "Solution 3", detail: 'Add another 21 ml of the rhodamine solution of 125 mg/L concentration to the pail and stir thoroughly for 20 seconds', status: '' },
    { steps: "Solution 4", detail: 'Add 50 ml of the turbidity solution of 4000 NTU concentration to the pail and stir thoroughly for 20 seconds', status: '' }
]);

//delay func
const wait = ms => new Promise<void>((resolve, err) => {
    setTimeout(() => resolve(), ms);
});

const retryWithDelay = async (
    fn, retries = 3, interval = 2000,
    finalErr = 'Retry failed'
) => {
    try {
        // try
        await fn();
    } catch (err) {
        // if no retries left
        // throw error
        if (retries <= 0) {
            return Promise.reject(finalErr);
        }

        //delay the next call
        await wait(interval);

        if (canceled.value === true) {
            return;
        }

        if (calibrationCurrentStep.value.toLowerCase() !== 'preparation') {
            return;
        }

        //recursively call the same func
        return retryWithDelay(fn, (retries - 1), interval, finalErr);
    }
}

const calibrationCurrentStep = ref("preparation");
const calibrationState = ref(0); // 0: not started. 1: started. 2: completed.
const solutionId = ref(0); // 0-3
const calibrationCompleteMessage = ref("");
const isReady = ref(false);
const currentAttempt = ref(0);
const maxAttempt = ref(720);
const canceled = ref(false);

const props = defineProps({
    id: {
        type: String,
        default: ''
    }
});

const preparationMessage = computed(() => {
    console.log(`Attempt=${currentAttempt.value}`);
    if (isReady.value) {
        return "Device is ready. Click next to start calibration";
    }
    else {
        let instruction = "Power cycle the Tracker and wait for connection confirmation ";
        if (currentAttempt.value > 0 && currentAttempt.value < maxAttempt.value) {
            instruction += `(${currentAttempt.value}/${maxAttempt.value})`;
        }
        if (currentAttempt.value >= maxAttempt.value) {
            instruction = `Still unable to connect to the device`;
        }

        return instruction;
    }
})

const calibrationMessage = computed(() => {
    if (calibrationState.value === 0) {
        return "Calibrate";
    }
    else if (calibrationState.value === 1) {
        return `Waiting for calibration ${solutionId.value + 1} to complete`;
    }
    else {
        return calibrationCompleteMessage.value;
    }
})

async function cancel() {
    completeCalibration();
}

function completeCalibration() {
    canceled.value = true;
    // Turn off awake
    calibrationCallFunction('off', 'stayAwakeMins', props.id);
    router.push({ name: `slot`, params: { id: props.id } });
}

async function calibrate(id: number) {
    calibrationCompleteMessage.value = "";
    solutionId.value = id;
    calibrationState.value = 1;
    const slotId = props.id;
    // Get Device ID


    const message = await callCalibrationFunction(id, slotId);
    calibrationCompleteMessage.value = message;
    items.value[id].status = message;
    calibrationState.value = 2;
}

const getPingFunc = () => {
    return async () => {
        await ping();
        currentAttempt.value += 1;
        if (isReady.value === false) {
            throw new Error('Not ready');
        }
    }
}

async function moveToStep(stepName, initialization) {
    if (calibrationCurrentStep.value.toLowerCase() === 'preparation' && initialization === false) { // Click away from preparation
        if (isReady.value === false) {
            return; // Cannot move away
        }
    }

    calibrationState.value = 0;
    calibrationCurrentStep.value = stepName;
    console.log(`Current step=${calibrationCurrentStep.value}`);
    if (calibrationCurrentStep.value.toLowerCase() === 'preparation') {
        await ping();
        if (!isReady.value) {
            await stayAwake();
            currentAttempt.value = 0;
            await retryWithDelay(getPingFunc(), maxAttempt.value);
        }
    }
}

async function stepChanged(currentStep, targetStep, delta) {
    await moveToStep(currentStep.targetStep.stepName, false);
}
async function stayAwake() {
    const slotId = props.id;
    const message = await callCalibarationAwakeFunction(60, slotId); // 60 minutes
}

async function ping() {
    const slotId = props.id;
    const pingResult = await pingDevice(slotId); // 60 minutes
    isReady.value = pingResult.ready;
    prepared.value = pingResult.ready ? 'ready' : '';

}

moveToStep("preparation", true);

</script>
<style scoped="true" lang="scss">
.home {
    display: flex;
    flex-direction: column;
    margin: auto;
    height: 100vh;
}

.buttons {
    display: flex;
    flex-direction: row;

}


#container {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
}

.action {
    margin: 0 auto;
    display: block;
    background-color: rgb(2, 117, 255);
}

.cancelBtn {
    margin: 0 auto;
    display: block;
    background-color: orange;  
}

h2,
h3,
h4 {
    color: white;
}


.table {
    width: 768px;
    color: white;
}
</style>